import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

import { listCatalogProductsByDrugNameApi } from 'api/drug';
import { buildPackaging, catalogName, compareAlphabetically } from '../utils/formatters';

const MIN_CHARS = 3;

// Catalog search, the same debounced picker four other screens already use
// (AssociateToCatalogModal, ResolveMultiRxReviewDialog, BulkLabelsFilterButton,
// useBulkOverride). No partner-scoped variant is needed: the exclusion below is
// the only partner-specific part, and the caller already knows which products the
// partner holds.
//
// Debounced with a timer ref rather than lodash.debounce, matching
// AddInventoryItemDialog: the test setup replaces lodash.debounce with a
// pass-through that has no .cancel(), so a cancel on unmount would throw.
const PreferenceDrugAutocomplete = ({ takenProductIds, value, onChange, disabled }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const runSearch = (term) => {
    if (term.length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    listCatalogProductsByDrugNameApi(term)
      .then((products) => setResults(products ?? []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  };

  const options = useMemo(
    () =>
      results
        .filter(
          // A preference is keyed on partner + product, so a catalog row with no
          // productId cannot carry one. And a product the partner already holds must
          // not be offered: the mutation is an upsert, so picking it would silently
          // replace the existing preference rather than add one.
          //
          // POM-6309: a product disabled for ordering is excluded outright, unlike the
          // order-working screens that show it and let it through. This screen configures
          // what a partner gets on FUTURE orders, and a product withdrawn from ordering
          // has none — so there is nothing here for the preference to apply to.
          (item) =>
            Boolean(item.productId) &&
            item.isCatalogEnabled !== false &&
            !takenProductIds.includes(item.productId)
        )
        // catalogName is what the option renders, so ordering by anything less leaves
        // same-name products in search-result order.
        .sort(compareAlphabetically(catalogName)),
    [results, takenProductIds]
  );

  const handleInputChange = (_, nextInput, reason) => {
    setInputValue(nextInput);
    if (reason !== 'input') return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => runSearch(nextInput.trim()), 300);
  };

  const tooShort = inputValue.trim().length < MIN_CHARS;

  const emptyText = () => {
    if (tooShort) return 'Type at least 3 characters';
    if (results.length === 0) return 'No catalog product matches';
    // Three zero-result cases, and the operator can only act on the right one: everything that
    // matched is already configured, or (POM-6309) everything that matched is disabled for
    // ordering and so has no future order for a preference to apply to.
    const isEveryMatchDisabled = results.every((item) => item.isCatalogEnabled === false);
    if (isEveryMatchDisabled) return 'Every match is disabled for new orders';

    return 'This partner already has a preference for every match';
  };

  return (
    <Autocomplete
      size='small'
      fullWidth
      disabled={disabled}
      options={options}
      loading={loading}
      value={value}
      inputValue={value ? catalogName(value) : inputValue}
      onChange={(_, next) => onChange(next)}
      onInputChange={handleInputChange}
      filterOptions={(opts) => opts}
      isOptionEqualToValue={(option, selected) => option.productId === selected.productId}
      getOptionLabel={(option) => catalogName(option)}
      noOptionsText={emptyText()}
      renderOption={(props, option) => {
        const packaging = buildPackaging(option);
        return (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <Box component='li' {...props} key={option.productId}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                {catalogName(option)}
              </Typography>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                <Box component='span' sx={{ fontFamily: 'monospace' }}>
                  {option.productId}
                </Box>
                {packaging ? ` · ${packaging}` : ''}
              </Typography>
              {option.hasNdc === false && (
                <Typography variant='caption' sx={{ color: 'warning.main', fontWeight: 600 }}>
                  No NDC in inventory &mdash; cannot be used as a preference
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          label='Drug'
          required
          placeholder='Search the catalog by drug name'
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color='inherit' size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

PreferenceDrugAutocomplete.propTypes = {
  takenProductIds: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.shape({ productId: PropTypes.string, drugName: PropTypes.string }),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

PreferenceDrugAutocomplete.defaultProps = {
  takenProductIds: [],
  value: null,
  disabled: false,
};

export default PreferenceDrugAutocomplete;
